import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, FormControl, InputLabel, OutlinedInput, InputAdornment, IconButton } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { getIcon } from '../../../utils/IconHandler';
import { changePassword } from '../../../services/UserService';

const ChangePasswordModal = ({ open, onClose }) => {
    const { t } = useTranslation();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [serverError, setServerError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setErrors({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setServerError('');
    }, [open]);

    const validate = () => {
        let tempErrors = { currentPassword: '', newPassword: '', confirmPassword: '' };
        let isValid = true;

        if (!currentPassword.trim()) {
            tempErrors.currentPassword = t('current_password_required');
            isValid = false;
        }

        if (!newPassword.trim()) {
            tempErrors.newPassword = t('new_password_required');
            isValid = false;
        }

        if (!confirmPassword.trim()) {
            tempErrors.confirmPassword = t('confirm_password_required');
            isValid = false;
        } else if (newPassword !== confirmPassword) {
            tempErrors.confirmPassword = t('passwords_must_match');
            isValid = false;
        }

        setErrors(tempErrors);
        return isValid;
    };

    const handleSave = async () => {
        if (validate()) {
            try {
                setLoading(true);
                await changePassword(currentPassword, newPassword);
                setLoading(false);
                onClose();
            } catch (error) {
                setLoading(false);
                setServerError(error.message || t('error_changing_password'));
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle><span className="roboto-light">{t('change_password')}</span></DialogTitle>
            <DialogContent style={{paddingTop:'15px'}}>
                <FormControl sx={{ width: '100%', marginBottom: 2 }} variant="outlined">
                    <InputLabel htmlFor="outlined-adornment-current-password">{t('current_password')}</InputLabel>
                    <OutlinedInput
                        id="outlined-adornment-current-password"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                                    {showCurrentPassword ? getIcon('eyeOff') : getIcon('eye')}
                                </IconButton>
                            </InputAdornment>
                        }
                        label={t('current_password')}
                        error={!!errors.currentPassword}
                    />
                    {errors.currentPassword && <span style={{ color: 'red' }}>{errors.currentPassword}</span>}
                </FormControl>
                <FormControl sx={{ width: '100%', marginBottom: 2 }} variant="outlined">
                    <InputLabel htmlFor="outlined-adornment-new-password">{t('new_password')}</InputLabel>
                    <OutlinedInput
                        id="outlined-adornment-new-password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>
                                    {showNewPassword ? getIcon('eyeOff') : getIcon('eye')}
                                </IconButton>
                            </InputAdornment>
                        }
                        label={t('new_password')}
                        error={!!errors.newPassword}
                    />
                    {errors.newPassword && <span style={{ color: 'red' }}>{errors.newPassword}</span>}
                </FormControl>
                <FormControl sx={{ width: '100%', marginBottom: 2 }} variant="outlined">
                    <InputLabel htmlFor="outlined-adornment-confirm-password">{t('confirm_password')}</InputLabel>
                    <OutlinedInput
                        id="outlined-adornment-confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                    {showConfirmPassword ? getIcon('eyeOff') : getIcon('eye')}
                                </IconButton>
                            </InputAdornment>
                        }
                        label={t('confirm_password')}
                        error={!!errors.confirmPassword}
                    />
                    {errors.confirmPassword && <span style={{ color: 'red' }}>{errors.confirmPassword}</span>}
                </FormControl>
                {serverError && <span style={{ color: 'red' }}>{serverError}</span>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('cancel')}</Button>
                <LoadingButton loading={loading} onClick={handleSave} variant="contained" color="primary">{t('update')}</LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ChangePasswordModal;
